import jsPDF from 'jspdf';
import 'jspdf-autotable';
import config from "../common/Config";
import { axiosFetchProformaInvoice } from '../../utils/proformaInvoiceApi';

const generateUniqueFilename = (baseName, extension) => {
  const timestamp = new Date().toISOString().replace(/[-:.]/g, "");
  return `${baseName}_${timestamp}.${extension}`;
};

const generateSummaryPDF = async (orderId, options = {}) => {
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem('token');
  const company_tax_type = localStorage.getItem("company_tax_type");
  const {
    targetWindow,
    existingDoc = null,
    startNewPage = false,
    skipAutoPrint = false,
  } = options;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);  // Convert timestamp string to Date object
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;  // Return formatted date as "YYYY-MM-DD"
  };

  try {
    const data = await axiosFetchProformaInvoice(orderId, token);

    if (!data.success || !data.details.orderDetails || data.details.orderDetails.length === 0) {
      console.error("No order details found in the response.");
      return { status: "error", doc: existingDoc };  // Return early if order details are not available
    }

   const orderDetails = data.details.orderDetails[0];       
const shopDetails = data.details.shop_details;     
const invoiceType = data.details.invoice_type;     
const deliveryDetails = data.details.delivery_details;
const displayVal = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "null" || text.toLowerCase() === "undefined") {
    return "";
  }
  return text;
};
const shopAddress = displayVal(shopDetails?.address);
const shopAddressParts = shopAddress ? shopAddress.split(",") : [""];
const customerAddress = displayVal(
  orderDetails.delivery_address ||
    orderDetails.address ||
    [orderDetails.city, orderDetails.state, orderDetails.country]
      .map(displayVal)
      .filter(Boolean)
      .join(", ")
);
const customerPhone = displayVal(orderDetails.phone);
const customerEmail = displayVal(orderDetails.email);
const deliveryDate = displayVal(
  orderDetails.deliverydate ||
    orderDetails.delivery_date ||
    ""
);
const invoiceDate = displayVal(
  orderDetails.deliverydate ||
    orderDetails.delivery_date ||
    orderDetails.invoice_date ||
    orderDetails.tax_invoice_date ||
    orderDetails.date
);
const customerName = displayVal(orderDetails.company || orderDetails.name);
const postalCode = displayVal(orderDetails.postal_code);
const orderBy = displayVal(orderDetails.orderby);
const accountNo = displayVal(orderDetails.account_no);
const ownOrderNumber = displayVal(orderDetails.own_order_number);
const shopName = displayVal(shopDetails?.shop_name);
const shopPhone = displayVal(shopDetails?.phone);
const shopEmail = displayVal(shopDetails?.email);
const shopWebsite = displayVal(shopDetails?.website);
const shopVat = displayVal(shopDetails?.vat_no);
const shopCompanyNo = displayVal(shopDetails?.company_reg_no);
const shopAwrs = displayVal(shopDetails?.awrs_no);
const shopBankAc = displayVal(shopDetails?.bank_ac);
const shopSortCode = displayVal(shopDetails?.sort_code);
const taxLabel = displayVal(company_tax_type) || "VAT";

const bundleDiscounts = Array.isArray(data.details.bundle_discounts) ? data.details.bundle_discounts : [];
let promoDiscount = 0;
let promoVat = 0;
bundleDiscounts.forEach((bundle) => {
  const fromBundlePromo = parseFloat(bundle.total_promo_discount ?? 0) || 0;
  const directPromoDiscount = parseFloat(bundle.total_discount ?? 0) || 0;
  const buyDiscount = parseFloat(bundle.buy_total_discount ?? 0) || 0;
  const getDiscount = parseFloat(bundle.get_total_discount ?? 0) || 0;
  promoDiscount +=
    fromBundlePromo > 0
      ? fromBundlePromo
      : directPromoDiscount > 0
        ? directPromoDiscount
        : buyDiscount + getDiscount;
  const fromBundlePromoVat = parseFloat(bundle.total_promo_discount_vat ?? 0) || 0;
  promoVat +=
    fromBundlePromoVat > 0
      ? fromBundlePromoVat
      : (parseFloat(bundle.buy_discount_vat ?? 0) || 0) +
        (parseFloat(bundle.get_discount_vat ?? 0) || 0);
});
if (promoDiscount <= 0.0001) {
  promoDiscount = parseFloat(orderDetails.total_promo_discount || 0) || 0;
}
if (promoVat <= 0.0001) {
  promoVat = parseFloat(orderDetails.total_promo_discount_vat || 0) || 0;
}
if (promoDiscount <= 0.0001) {
  promoDiscount = (data.details.product_details || []).reduce((sum, item) => {
    const amount =
      parseFloat(
        item.promotion_discount ??
          item.promo_disc_amount ??
          item.promo_discount_amount ??
          item.promo_discount ??
          0
      ) || 0;
    return sum + (amount > 0 ? amount : 0);
  }, 0);
}

// WITHOUT CONTRACT PRODUCTS
const totalstd_without_contract = data.details.product_details
  .filter(item => 
    item.item_tax !== '0.00' && 
    item.item_tax != null && 
    item.isContractPriceApplied === 'N'
  )
  .reduce((sum, item) => sum + parseFloat(item.subtotal), 0);

const totalzeroGoods_without_contract = data.details.product_details
  .filter(item => 
    (item.item_tax === '0.00' || item.item_tax === null) && 
    item.isContractPriceApplied === 'N'
  )
  .reduce((sum, item) => sum + parseFloat(item.subtotal), 0);

const totalTax_without_contract = data.details.product_details
  .filter(item => 
    item.item_tax !== '0.00' && 
    item.item_tax != null && 
    item.isContractPriceApplied === 'N'
  )
  .reduce((sum, item) => sum + (parseFloat(item.item_tax) || 0), 0);

// WITH CONTRACT PRODUCTS
const totalstd_with_contract = data.details.product_details
  .filter(item => 
    item.item_tax !== '0.00' && 
    item.item_tax != null && 
    item.isContractPriceApplied === 'Y'
  )
  .reduce((sum, item) => sum + parseFloat(item.subtotal), 0);

const totalzeroGoods_with_contract = data.details.product_details
  .filter(item => 
    (item.item_tax === '0.00' || item.item_tax === null) && 
    item.isContractPriceApplied === 'Y'
  )
  .reduce((sum, item) => sum + parseFloat(item.subtotal), 0);

const totalTax_with_contract = data.details.product_details
  .filter(item => 
    item.item_tax !== '0.00' && 
    item.item_tax != null && 
    item.isContractPriceApplied === 'Y'
  )
  .reduce((sum, item) => sum + (parseFloat(item.item_tax) || 0), 0);

// COMBINE TOTALS
const totalstd = totalstd_without_contract + totalstd_with_contract;
const totalzeroGoods = totalzeroGoods_without_contract + totalzeroGoods_with_contract;

const resolveLineOrderType = (item) =>
  String(item?.order_type || item?.type || "")
    .trim()
    .toLowerCase();
const box_qty = data.details.product_details
  .filter((item) => {
    const qty = parseFloat(item.quantity);
    return item.quantity != null && Number.isFinite(qty) && qty > 0 && resolveLineOrderType(item) === "box";
  })
  .reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

const piece_qty = data.details.product_details
  .filter((item) => {
    const qty = parseFloat(item.quantity);
    return item.quantity != null && Number.isFinite(qty) && qty > 0 && resolveLineOrderType(item) === "piece";
  })
  .reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);

// DISCOUNT CALCULATIONS
const totalSTDGoods = parseFloat(totalstd);
const orderDiscount1 = parseFloat(data.details.orderDetails[0].order_discount1) || 0;
const orderDiscount2 = parseFloat(data.details.orderDetails[0].over_all_discount) || 0;

// APPLY OVERALL DISCOUNT ONLY TO NON-CONTRACT PRODUCTS
const totalWithoutContract = totalstd_without_contract + totalzeroGoods_without_contract;
let orderDiscount3 = (totalWithoutContract * (parseFloat(data.details.orderDetails[0].over_all_discount))) / 100 || 0;

const lineVatTotal = totalTax_without_contract + totalTax_with_contract;
let totalVAT = lineVatTotal;
if (parseFloat(data.details.orderDetails[0].over_all_discount) > 0) {
  const vatDiscount =
    (totalTax_without_contract * parseFloat(data.details.orderDetails[0].over_all_discount)) / 100;
  totalVAT = lineVatTotal - vatDiscount;
}

// ITEM TOTAL
const itemTotal = totalstd + totalzeroGoods;

// OVERALL AMOUNT (only applies to non-contract products)
let over_all_amount = (totalWithoutContract * (parseFloat(data.details.orderDetails[0].over_all_discount))) / 100;

const grandTotal = parseFloat(orderDetails.grand_total || 0) || 0;
let totalAmount =
  grandTotal > 0
    ? grandTotal
    : totalstd +
      totalzeroGoods +
      totalVAT -
      over_all_amount -
      orderDiscount1 -
      (promoDiscount + promoVat);

    const addRibbonTitle = (doc, title, fontSize, pageWidth, margin, topMargin) => {
      // Set font and size for the title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
    
      // Set text color
      doc.setTextColor(0, 0, 0); // Set text color to black (RGB: 0, 0, 0)
    
      // Calculate the position for the text
      const titleWidth = doc.getTextWidth(title);
      const titleX = pageWidth - titleWidth - margin; // Align text to the right
      const titleY = topMargin + fontSize; // Add top margin to the Y position
    
      // Add the title text
      doc.text(title, titleX, titleY);
    };














    const doc = existingDoc || new jsPDF('portrait'); // Set to portrait orientation
    if (existingDoc && startNewPage) {
      doc.addPage();
    }
    const invoicePageStart = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 5;  // 5px padding
    let y = margin;
    const firstColumnWidth = pageWidth * 0.25;  // 30% of the page width
    const secondColumnWidth = pageWidth * 0.75; 
    //const H2firstColumnWidth = pageWidth * 0.45; 

    const H2firstColumnWidth = pageWidth * 0.40;  // 45% of the page width
    const H2secondColumnWidth = pageWidth * 0.25; // 27.5% of the page width
    const H2thirdColumnWidth = pageWidth * 0.30;  // 27.5% of the page width


    // Add the logo image to the header (make sure the image path is correct)
    const logoPrefix = accessToken || "atr";
    const logoUrl = `${config.BASE_URL || window.location.origin}/assets/img/${logoPrefix}_logo.png`;
    // Slightly larger square logo, centered in the left header column
    const logoWidth = 32;
    const logoHeight = 32;
    const logoColWidth = firstColumnWidth;
    const logoX = margin + Math.max(0, (logoColWidth - logoWidth) / 2);
    // Vertically center against the shop-details text block beside it
    const shopBlockTop = 5;
    const shopBlockHeight = 30;
    const logoTop = shopBlockTop + Math.max(0, (shopBlockHeight - logoHeight) / 2);
    try {
      const response = await fetch(logoUrl);
      if (!response.ok) {
        throw new Error(`Logo HTTP ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      try {
        const img = await new Promise((resolve, reject) => {
          const image = new window.Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = objectUrl;
        });
        const canvas = document.createElement("canvas");
        const maxSide = 256;
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const logoDataUrl = canvas.toDataURL("image/png");
        doc.addImage(logoDataUrl, "PNG", logoX, logoTop, logoWidth, logoHeight);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    } catch (logoError) {
      console.error("ATR invoice logo failed:", logoError);
    }
    y += Math.max(logoHeight, shopBlockHeight) + 5; // Adjust y position after adding the logo
   
    doc.setFont('helvetica', 'bold'); // Set font to bold
    doc.setFontSize(10); // Set font size
    const title = `${invoiceType || "Invoice"}`;
    const fontSize = 10;
    const topMargin = 0;
    addRibbonTitle(doc, title, fontSize, pageWidth, margin,topMargin);
     y += 10; // Adjust y position after adding the title and border // Now create the other sections of the header
    const boldTxt = '<b>';
    const boldTxtClose = '</b>';

    const headerSections2 = [
      [
        // Add shop details (below logo)
        ],
      [
        { text: shopName, size: 13, bold: true },
         { text: `${shopAddressParts[0] || ""}`.trim(), size: 8, bold: false }, // First line of address
    { text: `${shopAddressParts.slice(1).join(",").trim()}`, size: 8, bold: false }, // Second line of address
    { text: `Company No : ${shopCompanyNo} | ${taxLabel} No : ${shopVat}`, size: 8, bold: false },
    { text: `Phone No : ${shopPhone} | Email : ${shopEmail}`, size: 8, bold: false },
    { text: `Website : ${shopWebsite} | AWRS No : ${shopAwrs}`, size: 8, bold: false },

    ],
    // [
    // { text: `PLEASE PHONE THE OFFICE TO `, size: 8, bold: false,align: 'right'},
    // { text: `PAY BY CREDIT/DEBIT CARD `, size: 8, bold: false ,align: 'right' },
    // ]

      ];





    const headerSections3 = [
      [
        { text: ``, size: 8, bold: true },
        { text: customerName, size: 12, bold: true },
        { text: ``, size: 8, bold: true },
        { text: customerAddress, size: 8, bold: false },
        { text: ``, size: 8, bold: true },
        { text: postalCode, size: 8, bold: false },
        { text: `Phone No : ${customerPhone} | Email : ${customerEmail}`, size: 8, bold: false },
        
        ],
      [

        { text: `Invoice No : ${displayVal(orderDetails.reference_no)}`, size: 8, bold: false },
        ...(ownOrderNumber ? [{ text: `Your own order number : ${ownOrderNumber}`, size: 8, bold: false }] : []),
        { text: `Invoice Date: ${invoiceDate}`, size: 8, bold: false },
        { text: `A/C No: ${accountNo}`, size: 8, bold: false },
        { text: `Delivery Date : ${deliveryDate}`, size: 8, bold: false },
        { text: `Order By: ${orderBy}`, size: 8, bold: false },

        ],
      [
        { text: `Bank Details`, size: 8, bold: true },
        { text: shopName, size: 8, bold: true },
        { text: ``, size: 8, bold: false },
        { text: `A/C No : ${shopBankAc}`, size: 8, bold: false},
        { text: `Sort Code: ${shopSortCode}`, size: 8, bold: false},

        ],


      ];




    const sectionWidth = (pageWidth - margin * 2) / 2; // Adjust for margins

    // Render header sections
    y = 7;
   const renderSections = (sections, marginTop = 7) => { // marginTop is an argument that defaults to 10
  let yPosition = y + marginTop; // Add initial marginTop space before rendering starts

  sections.forEach((section, index) => {
    // Calculate starting X position for the columns (for layout)
    const colStartX = margin + (index % 3) * firstColumnWidth;

    section.forEach((item, i) => {
      doc.setFontSize(item.size);
      doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
      doc.text(displayVal(item.text), colStartX, yPosition + i * 4); // Adjust Y based on current position
    });

    // Increase the Y position after rendering each section
    if (index % 4 === 3) {
      yPosition += 12; // Adjust spacing between sections as needed
    }
  });

  // Add a little space after the last section
  yPosition += 25;

  // Update the global Y value if you need to keep track of the Y position globally
  y = yPosition;
};

renderSections(headerSections2);
// Keep customer boxes below the logo so they do not overlap
y = Math.max(y, logoTop + logoHeight + 2);

    const h2renderSections = (sections) => {
  let rowHeight = 0; // To store the maximum height of columns in a row
  let rowStartY = y; // Track the starting Y position for each row of 3 columns
  let columnHeights = []; // To store individual column heights within a row

  sections.forEach((section, index) => {
    // Column X position based on section index (for a 3-column layout)
    let colStartX;
    if (index % 3 === 0) {
      colStartX = margin;
    } else if (index % 3 === 1) {
      colStartX = margin + H2firstColumnWidth;
    } else {
      colStartX = margin + H2firstColumnWidth + H2secondColumnWidth;
    }

    let sectionHeight = 0; // To track the height of the current section

    section.forEach((item, i) => {
      const itemText = displayVal(item.text);
      doc.setFontSize(item.size);
      doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');

      // Wrap text if it exceeds column width (maxWidth)
      let maxWidth;
      if (index % 3 === 0) {
        maxWidth = H2firstColumnWidth - 5; // First column width
      } else if (index % 3 === 1) {
        maxWidth = H2secondColumnWidth - 5; // Second column width
      } else {
        maxWidth = H2thirdColumnWidth - 5; // Third column width
      }

      const textWidth = doc.getTextWidth(itemText);
      let lines = [];
      if (textWidth > maxWidth && itemText) {
        // Split text into multiple lines
        let words = itemText.split(' ');
        let line = '';

        words.forEach((word) => {
          const testLine = line + (line ? ' ' : '') + word;
          if (doc.getTextWidth(testLine) <= maxWidth) {
            line = testLine;
          } else {
            lines.push(line);
            line = word;
          }
        });
        lines.push(line); // Push remaining line
      } else {
        lines.push(itemText); // No wrapping needed, just one line
      }

      // Render each line of text
      lines.forEach((lineText, lineIndex) => {
        const lineHeight = 4; // Adjust vertical spacing for each line
        doc.text(lineText, colStartX + 5, y + 5 + i * lineHeight + lineIndex * lineHeight);
        sectionHeight = y + i + lineIndex - 5; // Update section height
      });
    });

    // Store the height of the current section (column height)
    columnHeights.push(sectionHeight);

    // After every 3 columns (3 sections), calculate the maximum height for this row
    if ((index + 1) % 3 === 0) {
      rowHeight = Math.max(...columnHeights); // Get the maximum height in this row
      columnHeights = []; // Reset for the next row

      // Draw borders for all 3 columns in the row
      let colX = margin;
      for (let i = 0; i < 3; i++) {
        doc.setLineWidth(0.1); // Border line width
        let colWidth = (i === 0) ? H2firstColumnWidth : (i === 1) ? H2secondColumnWidth : H2thirdColumnWidth;
        doc.rect(colX, rowStartY - 2, colWidth, rowHeight + 0); // Draw the rectangle for the column with a small padding
        colX += colWidth; // Update X position for the next column
      }

      // Move the Y position down for the next row of columns
      y += rowHeight + 12; // Add some space after each row of columns
    }
  });

  y += -3; // Add a little space after all header sections
};

// Call the function to render sections with equal height borders
h2renderSections(headerSections3);






    // Render table columns with customized widths
const tableColumns = [
  { title: 'Product', dataKey: 'product', size: 8, bold: true, halign: 'center' },
  { title: 'Type', dataKey: 'order_type', size: 8, bold: true, halign: 'center' },
  { title: 'Qty', dataKey: 'qty', size: 8, bold: true, halign: 'center' },
  { title: 'Price', dataKey: 'unit_price', size: 8, bold: true, halign: 'center' },
  { title: 'Disc', dataKey: 'discount', size: 8, bold: true, halign: 'center' },
  { title: `${company_tax_type}`, dataKey: 'vat', size: 8, bold: true, halign: 'center' },
  { title: 'Total', dataKey: 'net_amount', size: 8, bold: true, halign: 'center' },
  ];
    // Calculate the table widths
const totalWidth = pageWidth - 2 * margin;
    const productWidth = totalWidth * 0.45;  // 45% of the page width for product name/code
    const orderTypeWidth = totalWidth * 0.06;  // 5% for Order Type
    const qtyWidth = totalWidth * 0.06;  // 5% for Qty
    const unitPriceWidth = totalWidth * 0.12;  // 13% for Unit Price
    const discountWidth = totalWidth * 0.08;  // 6% for Discount
    const vatWidth = totalWidth * 0.11;  // 13% for VAT
    const netAmountWidth = totalWidth * 0.12; 

    const tableBody = [];
    (data.details.product_details || []).forEach((item) => {
      const qtyNum = parseFloat(item.quantity);
      const qtyDisplay = Number.isFinite(qtyNum)
        ? (Number.isInteger(qtyNum) ? String(qtyNum) : qtyNum.toFixed(2))
        : item.quantity;
      const hasPromotion =
        item.is_promoted === "1" ||
        item.is_promoted === 1 ||
        Number(item.promo_id) > 0 ||
        Number(item.promoId) > 0 ||
        Number(item.promosId) > 0 ||
        Number(item.promotion_id) > 0 ||
        Number(item.promotionId) > 0;
      const originalPrice = parseFloat(item.original_price || item.unit_price || 0);
      const subtotal = parseFloat(item.subtotal || 0);
      const isFree =
        hasPromotion &&
        (item.free_product === "1" ||
          item.free_product === 1 ||
          item.free_product === "(F)" ||
          item.free_product === "Y" ||
          originalPrice === 0 ||
          Math.abs(subtotal) < 0.000001);
      const isPromotedP = hasPromotion && !isFree;
      const promoName = String(item.promotion_name || item.promo_name || "").trim();
      const promoAmount =
        parseFloat(
          item.promotion_discount ??
            item.promo_disc_amount ??
            item.promo_discount_amount ??
            item.promo_discount ??
            0
        ) || 0;
      const rawItemDisc = parseFloat(item.item_discount || 0) || 0;
      const discDisplay =
        isPromotedP && promoAmount > 0.0001 ? "0.00" : rawItemDisc.toFixed(2);
      tableBody.push([
        item.product_code + ' - ' + item.product_name + ' - ' + item.size + item.promo_product + item.free_product + item.ContractPrice,
        item.order_type,
        qtyDisplay,
        '£'+item.unit_price,
        discDisplay,
        '£'+item.item_tax,
        '£'+item.subtotal,
      ]);
      if (isPromotedP && promoName && promoAmount > 0.0001) {
        tableBody.push([
          {
            content: promoName,
            colSpan: 6,
            styles: { fontStyle: "bold", fontSize: 8, halign: "left" },
          },
          {
            content: `-£${promoAmount.toFixed(2)}`,
            styles: { fontStyle: "bold", fontSize: 8, halign: "right" },
          },
        ]);
      }
    });

    // Add an empty row with totals
    tableBody.push([
      { content: 'Item Total:', colSpan: 6, styles: { halign: 'right' } },
      {content : `£ ${itemTotal.toFixed(2)}`} 
      
      ]);

    tableBody.push([
      { content: `Total Zero VAT Goods - £ ${totalzeroGoods.toFixed(2)}  | Total VAT Goods - £ ${totalstd.toFixed(2)}`, colSpan: 5, styles: { halign: 'left' } }, 
      { content: `${company_tax_type} Amount:`,  styles: { halign: 'right',fontSize :8,fontStyle: 'bold' } }, 
      {content : `£ ${totalVAT.toFixed(2)}` }

      ]);


if (orderDiscount2 != 0 && orderDiscount2 !== '') {
    tableBody.push([
        { content: `Order Discount (${orderDiscount2}%):`, colSpan: 6, styles: { halign: 'right', fontSize: 8, fontStyle: 'bold' } },
        orderDiscount3.toFixed(2)
    ]);
}

// if (bundleDiscounts.length > 0) {
//     tableBody.push([
//         { content: `Promo Discount :`, colSpan: 6, styles: { halign: 'right', fontSize: 8, fontStyle: 'bold' } },
//         `£ ${promoDiscount.toFixed(2)}`
//     ]);
//     tableBody.push([
//         { content: `Promo VAT :`, colSpan: 6, styles: { halign: 'right', fontSize: 8, fontStyle: 'bold' } },
//         `£ ${promoVat.toFixed(2)}`
//     ]);
// }

tableBody.push([
  { content:  `Box : ${box_qty} PCs : ${piece_qty} `, colSpan: 5, styles: { halign: 'left' } }, 
  { content: 'Invoice Total:', styles: { halign: 'right',fontSize :8,fontStyle: 'bold' } }, 
  {content : `£ ${totalAmount.toFixed(2)}` }
]);

doc.autoTable({
  startY: y - 8,
  head: [tableColumns.map(col => col.title)],
  body: tableBody,
  theme: 'grid',
  headStyles: {
    fillColor: '#f8f9fa',
    textColor: '#404040',
    fontSize: 8,  
    valign: 'middle', 
    lineWidth: 0.3,
    halign: 'center', 
    lineWidth: 0.3,
    lineColor: [0, 0, 0] 
  },
  bodyStyles: {
    fontSize: 8,  
    valign: 'middle',
    lineWidth: 0.3,
    lineColor: [0, 0, 0] 
  },
  margin: { top: 0, left: margin, right: margin },
  columnStyles: {
    0: { cellWidth: productWidth, fontSize: 8, fontStyle: 'bold' },
    1: { cellWidth: orderTypeWidth,fontSize: 8, fontStyle: 'bold' },
    2: { cellWidth: qtyWidth ,fontSize: 8, fontStyle: 'bold'},
    3: { cellWidth: unitPriceWidth ,fontSize: 8, fontStyle: 'bold'},
    4: { cellWidth: discountWidth ,fontSize: 8, fontStyle: 'bold'},
    5: { cellWidth: vatWidth ,fontSize: 8, fontStyle: 'bold'},
    6: { cellWidth: netAmountWidth ,fontSize: 8, fontStyle: 'bold' },
    1: { halign: 'center' },
    2: { halign: 'center' },
    3: { halign: 'right' },
    4: { halign: 'center' },
    5: { halign: 'right' },
    6: { halign: 'right' },
  },
});

    y = doc.autoTable.previous.finalY + 5;

    const addFooterSection = (footerSection) => {
      footerSection.forEach((section, index) => {
        const colStartX = margin + (index % 4) * sectionWidth;

        section.forEach((item, i) => {
          doc.setFontSize(item.size);
          doc.setFont(item.bold ? 'helvetica' : 'helvetica', item.bold ? 'bold' : 'normal');
          doc.text(item.text, colStartX, y + i * 5);
        });

        if (index % 4 === 3) {
          y += 20; 
        }
      });
      y += 25; 
    };

    const addFooterWithSpaceCheck = (footerSection) => {
      const remainingSpace = pageHeight - y - margin; 
      const requiredSpace = footerSection.length * 5 + 20; 

      if (remainingSpace < requiredSpace) {
        doc.addPage(); 
        y = margin ;
      }
      
      addFooterSection(footerSection);
      y += 10; 
    };

    const footerSections2 = [
      [

        { text: 'Delivery Checked & Collected By : _ _ _ _ _ _ _ _ _',size: 8, bold: false },
        { text: 'Amt Paid :_ _ _ _ _ _ _ _ _ _Collected By :_ _ _ _ _ _ _ _ _ _ ', size: 8, bold: false },
        { text: 'Terms -', size: 8, bold: false },
        { text: `1 - Once delivery is received ${shopDetails.shop_name} is not responsible for any damage or loss `, size: 8, bold: false },
        { text: `2 - Goods remain the property of ${shopDetails.shop_name} until invoice fully paid `, size: 8, bold: false },
        { text: `3 - No claims for damages or short delivery will be considered unless made at the time of delivery of goods.`, size: 8, bold: false },
        { text: `4 - There is No Refund Policy Of goods sold`, size: 8, bold: false },
        ]
      ];

// Add footerSections2 normally, ensuring it is added in the available space
    addFooterWithSpaceCheck(footerSections2);

 const footerSections3 = [
  [
    { text: `Staff Note: ${orderDetails.staff_note}`, size: 8, bold: true },
     { text: `Note For Driver: ${orderDetails.note_for_driver}`, size: 8, bold: true },
  ]
]; 

    addFooterWithSpaceCheck(footerSections3);
if (Array.isArray(deliveryDetails) && deliveryDetails.length > 0) {
    // Add "Goods Collected By" and "Signature" box at the bottom
    const boxMarginTop = 5;
const boxHeight = 20;
const boxWidth = pageWidth - 2 * margin;
let boxY = y + boxMarginTop;

// Ensure box fits on page, else add new page
if (boxY + boxHeight > pageHeight - margin) {
  doc.addPage();
  boxY = margin + boxMarginTop;
}

// Draw the box
doc.setLineWidth(0.2);
doc.rect(margin, boxY, boxWidth, boxHeight);

// Add the labels inside the box
doc.setFontSize(10);
doc.setFont('helvetica', 'bold');
let collectedByText = 'Goods Collected By:';
let signatureText = 'Signature:';

// If deliveryDetails is present, use dynamic values

  const recipient = deliveryDetails[0];
  if (recipient.received_by) {
    collectedByText = `Goods Collected By: ${recipient.received_by}`;
  }


doc.text(collectedByText, margin + 5, boxY + 15);
doc.text(signatureText, margin + boxWidth / 2 + 5, boxY + 15);

// Remove lines for writing/signature
// doc.setLineWidth(0.2);
// doc.line(margin + 40, boxY + 15, margin + boxWidth / 2 - 10, boxY + 15); // Line for "Goods Collected By"
 doc.line(margin + boxWidth / 2 + 25, boxY + 15, margin + boxWidth - 10, boxY + 15); // Line for "Signature"

// If deliveryDetails has a signature, add it above the signature label
if (Array.isArray(deliveryDetails) && deliveryDetails.length > 0) {
  const recipient = deliveryDetails[0];
  if (recipient.recipient_signature) {
    const sigWidth = 15; // smaller width
    const sigHeight = 10; // smaller height
    const sigAreaStart = margin + boxWidth / 2 + 15;
    const sigAreaEnd = margin + boxWidth - 10;
    const sigX = sigAreaStart + ((sigAreaEnd - sigAreaStart) - sigWidth) / 2;
    const sigY = boxY + 4;
    doc.addImage(recipient.recipient_signature, 'PNG', sigX, sigY, sigWidth, sigHeight);
  }
}

y = boxY + boxHeight + 5; // Update y for any further content
}
  

    const addPageNumbers = (doc, pageStart = 1, pageEnd = null) => {
      const end = pageEnd || doc.internal.getNumberOfPages();
      const total = Math.max(1, end - pageStart + 1);
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      for (let i = pageStart; i <= end; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        const localPage = i - pageStart + 1;
        const pageNumberText = `Page ${localPage} of ${total}`;
        const textWidth = doc.getTextWidth(pageNumberText);
        const xPosition = pageWidth - textWidth - 10;
        doc.text(pageNumberText, xPosition, pageHeight - 12);
      }
    };
const remainingSpace = pageHeight - y - 10; // Calculate available space

// Add footer content with direct positioning
if (remainingSpace >= 20) {
 // doc.text("No claims for damages or short delivery will be considered unless made at the time of delivery of goods. These goods are not sold on a sale or return basis.", 10, pageHeight - 20);  // Directly specify position
} else {

 // doc.addPage(); 
 // doc.text("No claims for damages or short delivery will be considered unless made at the time of delivery of goods. These goods are not sold on a sale or return basis.", 10, pageHeight - 20);  // Directly specify position on new page
}
const invoicePageEnd = doc.internal.getNumberOfPages();
if (skipAutoPrint) {
  return {
    status: "ok",
    doc,
    pageStart: invoicePageStart,
    pageEnd: invoicePageEnd,
  };
}

addPageNumbers(doc, invoicePageStart, invoicePageEnd);
const filename = generateUniqueFilename('Invoice', 'pdf');
//doc.save(filename);

doc.autoPrint();
const blobUrl = doc.output('bloburl');
let printWin = targetWindow && !targetWindow.closed ? targetWindow : null;
if (printWin) {
  try {
    printWin.location.href = blobUrl;
  } catch (e) {
    printWin = window.open(blobUrl);
  }
} else {
  printWin = window.open(blobUrl);
}

if (!printWin) {
  console.error('Print window was blocked');
  return { status: 'blocked', doc };
}

const waitStatus = await new Promise((resolve) => {
  let settled = false;
  const finish = (status) => {
    if (settled) return;
    settled = true;
    clearTimeout(timer);
    clearInterval(poll);
    try {
      window.removeEventListener("focus", onFocus);
    } catch (e) {}
    resolve(status);
  };
  const onFocus = () => {
    // Print dialog closed → focus returns to the app
    setTimeout(() => finish("done"), 250);
  };
  const timer = setTimeout(() => finish("timeout"), 20000);
  try {
    printWin.addEventListener("afterprint", () => finish("done"));
  } catch (e) {}
  try {
    window.addEventListener("focus", onFocus);
  } catch (e) {}
  const poll = setInterval(() => {
    try {
      if (printWin.closed) finish("done");
    } catch (e) {
      finish("done");
    }
  }, 400);
});

return { status: waitStatus, doc };



} catch (error) {
  console.error('Error generating PDF:', error);
  return { status: 'error', doc: existingDoc };
}
};

const openAutoPrintPdf = async (doc, targetWindow = null, invoicePageRanges = []) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const ranges =
    Array.isArray(invoicePageRanges) && invoicePageRanges.length > 0
      ? invoicePageRanges
      : [
          {
            start: 1,
            end: doc.internal.getNumberOfPages(),
          },
        ];

  ranges.forEach(({ start, end }) => {
    const total = Math.max(1, end - start + 1);
    for (let page = start; page <= end; page += 1) {
      doc.setPage(page);
      doc.setFontSize(8);
      const localPage = page - start + 1;
      const pageNumberText = `Page ${localPage} of ${total}`;
      const textWidth = doc.getTextWidth(pageNumberText);
      const xPosition = pageWidth - textWidth - 10;
      doc.text(pageNumberText, xPosition, pageHeight - 12);
    }
  });

  doc.autoPrint();
  const blobUrl = doc.output("bloburl");
  let printWin = targetWindow && !targetWindow.closed ? targetWindow : null;
  if (printWin) {
    try {
      printWin.location.href = blobUrl;
    } catch (e) {
      printWin = window.open(blobUrl);
    }
  } else {
    printWin = window.open(blobUrl);
  }
  if (!printWin) return { status: "blocked", printWin: null };

  const status = await new Promise((resolve) => {
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      clearInterval(poll);
      try {
        window.removeEventListener("focus", onFocus);
      } catch (e) {}
      resolve(value);
    };
    const onFocus = () => setTimeout(() => finish("done"), 250);
    const timer = setTimeout(() => finish("timeout"), 120000);
    try {
      printWin.addEventListener("afterprint", () => finish("done"));
    } catch (e) {}
    try {
      window.addEventListener("focus", onFocus);
    } catch (e) {}
    const poll = setInterval(() => {
      try {
        if (printWin.closed) finish("done");
      } catch (e) {
        finish("done");
      }
    }, 400);
  });

  return { status, printWin };
};

export async function printAllAtrInvoices(orderIds, options = {}) {
  const ids = (orderIds || []).map(String).filter(Boolean);
  const { targetWindow = null, onProgress = null } = options;
  if (!ids.length) return { status: "empty" };

  let doc = null;
  let printedCount = 0;
  const invoicePageRanges = [];

  for (let i = 0; i < ids.length; i += 1) {
    if (typeof onProgress === "function") {
      onProgress({ current: i + 1, total: ids.length });
    }
    const result = await generateSummaryPDF(ids[i], {
      existingDoc: doc,
      startNewPage: Boolean(doc),
      skipAutoPrint: true,
    });
    if (!result?.doc || result.status === "error") {
      continue;
    }
    doc = result.doc;
    if (result.pageStart && result.pageEnd) {
      invoicePageRanges.push({
        start: result.pageStart,
        end: result.pageEnd,
      });
    }
    printedCount += 1;
  }

  if (!doc || printedCount === 0) {
    return { status: "error", printWin: targetWindow };
  }

  const opened = await openAutoPrintPdf(doc, targetWindow, invoicePageRanges);
  return {
    status: opened.status,
    printWin: opened.printWin,
    count: printedCount,
  };
}

export default generateSummaryPDF;
